import { useEffect, useRef, useState } from 'react';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { safeHttpUrl } from '@/lib/safeUrl';
import type { LinkPreview } from '@flock-types/chat.types';

const X_COLLAPSED_HEIGHT = 210;

declare global {
  interface Window {
    twttr?: {
      widgets: {
        createTweet: (
          id: string,
          el: HTMLElement,
          options?: Record<string, unknown>
        ) => Promise<HTMLElement | undefined>;
      };
      ready?: (cb: () => void) => void;
    };
  }
}

let widgetsPromise: Promise<void> | null = null;

/** Load Twitter's widgets.js once (same script mobile injects into its WebView). */
function loadTwitterWidgets(): Promise<void> {
  if (window.twttr?.widgets) return Promise.resolve();
  if (widgetsPromise) return widgetsPromise;
  widgetsPromise = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://platform.twitter.com/widgets.js';
    script.async = true;
    script.charset = 'utf-8';
    script.onload = () => resolve();
    script.onerror = () => {
      widgetsPromise = null;
      reject(new Error('widgets.js failed to load'));
    };
    document.head.appendChild(script);
  });
  return widgetsPromise;
}

interface Props {
  preview: LinkPreview;
  own: boolean;
}

/**
 * Real X post embed via Twitter's widget (mobile uses the same widget inside
 * a WebView). Collapses tall posts at 210px with a Show more toggle, "X post"
 * footer — matching XEmbedCard.tsx on mobile. Falls back to a plain link
 * card if the widget fails (deleted/protected post, script blocked).
 */
export default function XEmbedCard({ preview, own }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'failed'>('loading');
  const [expanded, setExpanded] = useState(false);
  const [contentHeight, setContentHeight] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const el = containerRef.current;
    if (!el || !preview.providerId) {
      setStatus('failed');
      return;
    }

    // Fallback readiness signal: createTweet's promise can hang on some
    // widget builds — flip to ready as soon as the widget injects DOM.
    const readyObserver = new MutationObserver(() => {
      if (el.childElementCount > 0) {
        setStatus((s) => (s === 'loading' ? 'ready' : s));
        setContentHeight(el.scrollHeight);
      }
    });
    readyObserver.observe(el, { childList: true, subtree: true });

    loadTwitterWidgets()
      .then(() => {
        if (cancelled || !window.twttr?.widgets) return;
        return window.twttr.widgets.createTweet(preview.providerId!, el, {
          dnt: true,
          theme: 'light',
          align: 'center',
        });
      })
      .then((widget) => {
        if (cancelled) return;
        if (widget) {
          setStatus('ready');
          setContentHeight(el.scrollHeight);
        } else if (el.childElementCount === 0) {
          setStatus('failed');
        }
      })
      .catch(() => {
        if (!cancelled && el.childElementCount === 0) setStatus('failed');
      });

    return () => {
      cancelled = true;
      readyObserver.disconnect();
    };
  }, [preview.providerId]);

  const openUrl = safeHttpUrl(preview.canonicalUrl) || safeHttpUrl(preview.url);

  if (status === 'failed') {
    const Wrapper = openUrl ? 'a' : 'div';
    const wrapperProps = openUrl
      ? { href: openUrl, target: '_blank', rel: 'noopener noreferrer' }
      : {};

    return (
      <Wrapper
        {...wrapperProps}
        className={clsx(
          'mt-2 block overflow-hidden rounded-xl border bg-white px-3 py-2 text-xs',
          own ? 'border-white/20' : 'border-flock-navy-200'
        )}
      >
        <div className="font-semibold text-flock-navy-900">
          {preview.authorName ? `Post by ${preview.authorName}` : 'X post'}
        </div>
        <div className="mt-0.5 text-flock-navy-400">View on X (formerly Twitter)</div>
      </Wrapper>
    );
  }

  const canCollapse = contentHeight > X_COLLAPSED_HEIGHT + 40;
  const collapsed = canCollapse && !expanded;

  return (
    <div
      className={clsx(
        'mt-2 w-80 max-w-full overflow-hidden rounded-xl border bg-white',
        own ? 'border-white/20' : 'border-flock-navy-200'
      )}
    >
      <div
        className="relative overflow-hidden px-2 pt-2"
        style={collapsed ? { maxHeight: X_COLLAPSED_HEIGHT } : undefined}
      >
        {status === 'loading' && (
          <div className="flex h-32 items-center justify-center text-xs text-flock-navy-400">
            Loading post…
          </div>
        )}
        <div ref={containerRef} />
        {collapsed && (
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-white to-transparent" />
        )}
      </div>

      {canCollapse && (
        <button
          onClick={() => setExpanded((v) => !v)}
          className="flex w-full items-center justify-center gap-1 border-t border-flock-gray-100 py-1.5 text-xs font-medium text-flock-navy-600 hover:bg-flock-navy-50"
        >
          {expanded ? 'Show less' : 'Show more'}
          {expanded ? <ChevronUpIcon className="h-3.5 w-3.5" /> : <ChevronDownIcon className="h-3.5 w-3.5" />}
        </button>
      )}

      {openUrl ? (
        <a
          href={openUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-between border-t border-flock-gray-100 px-3 py-2 text-xs text-flock-navy-500 hover:bg-flock-navy-50"
        >
          <span className="font-medium">X post</span>
          <span>Open on X ↗</span>
        </a>
      ) : (
        <div className="flex items-center justify-between border-t border-flock-gray-100 px-3 py-2 text-xs text-flock-navy-500">
          <span className="font-medium">X post</span>
        </div>
      )}
    </div>
  );
}
